import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User as UserIcon, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { apiErr } from "../lib/api";
import { Input, Field } from "../components/form";
import { toast } from "sonner";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register({ email: form.email, password: form.password, name: form.name, role: "agent" });
      nav("/");
    } catch (err) {
      toast.error(apiErr(err));
    }
    setLoading(false);
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 bg-navy-900 relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/70 to-navy-900/40" />
        <div className="relative flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center shadow-lg p-1.5 shrink-0">
            <img src="/brand/akinita-mark.png" alt="Akinita" className="w-full h-full object-contain" data-testid="brand-logo-mark" />
          </div>
          <div>
            <p className="font-display font-bold text-white text-lg leading-tight" data-testid="brand-slogan">Akinita Real Estate Intelligence</p>
            <p className="text-[11px] uppercase tracking-widest text-gold/80 font-semibold">by Global Advancement Solutions</p>
          </div>
        </div>
        <div className="relative">
          <div className="inline-block bg-white rounded-2xl p-4 mb-8 shadow-2xl">
            <img src="/brand/akinita-logo.png" alt="Akinita — Real Estate Intelligence" className="w-56 xl:w-64 object-contain" data-testid="brand-logo-full" />
          </div>
          <h1 className="font-display text-4xl xl:text-5xl font-extrabold text-white leading-tight">
            Every lead, property & conversation.<br /><span className="text-gold">One command center.</span>
          </h1>
          <p className="text-slate-300 mt-5 max-w-md leading-relaxed">
            From FSBO prospecting to closing — connect people, properties, and pipelines in a single searchable system built for high-velocity dealmaking.
          </p>
        </div>
        <div className="relative text-xs text-slate-400">LEAD → CONTACT → CONVERSATION → PROPERTY → FOLLOW-UP → OPPORTUNITY → TRANSACTION → CLOSING</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/brand/akinita-mark.png" alt="Akinita" className="w-12 h-12 object-contain" />
            <div><p className="font-display font-bold text-navy-800 leading-tight">Akinita Real Estate Intelligence</p><p className="text-[10px] uppercase tracking-widest text-gold-dark font-semibold">by Global Advancement Solutions</p></div>
          </div>
          <h2 className="font-display text-3xl font-extrabold text-navy-800">{mode === "login" ? "Welcome back" : "Create account"}</h2>
          <p className="text-sm text-slate-500 mt-1.5 mb-8">{mode === "login" ? "Sign in to your real estate command center" : "Get started with your CRM workspace"}</p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Field label="Full name">
                <div className="relative"><UserIcon className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                  <Input value={form.name} onChange={set("name")} placeholder="Jane Realtor" required className="pl-9" data-testid="register-name" /></div>
              </Field>
            )}
            <Field label="Email">
              <div className="relative"><Mail className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                <Input type="email" value={form.email} onChange={set("email")} placeholder="you@brokerage.com" required className="pl-9" data-testid="login-email" /></div>
            </Field>
            <Field label="Password">
              <div className="relative"><Lock className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                <Input type="password" value={form.password} onChange={set("password")} placeholder="••••••••" required className="pl-9" data-testid="login-password" /></div>
            </Field>
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full bg-navy-800 hover:bg-navy-700 text-white font-semibold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? "Please wait…" : (mode === "login" ? "Sign in" : "Create account")}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-slate-200" /><span className="text-xs text-slate-400">or</span><div className="flex-1 h-px bg-slate-200" />
          </div>
          <button onClick={google} data-testid="google-login-btn"
            className="w-full border border-slate-200 bg-white hover:bg-slate-50 text-navy-800 font-medium py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2">
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continue with Google
          </button>

          <p className="text-center text-sm text-slate-500 mt-6">
            {mode === "login" ? "New to the platform? " : "Already have an account? "}
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-gold-dark font-semibold hover:underline" data-testid="toggle-auth-mode">
              {mode === "login" ? "Create account" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
